import { trpc } from '@/lib/trpc';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Wish } from '../wish';
import { UpsertResponse } from './useUpsertWish';
import { toast } from 'sonner';

const useMarkAsGifted = () => {
	const { t } = useTranslation();
	const utils = trpc.useUtils();
	const { id_list } = useParams();

	const { mutate, isPending } = useMutation({
		mutationFn: async (wish: Wish) => {
			const formData = new FormData();
			formData.append('id', wish.id.toString());
			formData.append('name', wish.name);
			formData.append('comment', wish.comment || '');
			formData.append('price', wish.price?.toString() || '');
			formData.append('url', wish.url || '');
			formData.append('fk_list', id_list as string);
			formData.append(
				'image_url',
				wish.image_key
					? `${import.meta.env.VITE_API_URL}/image?imageKey=${wish.image_key}`
					: ''
			);
			formData.append('image_key', wish?.image_key || '');
			formData.append('status', 'gifted');

			const update = await fetch(`${import.meta.env.VITE_API_URL}/wish`, {
				method: 'POST',
				body: formData,
				credentials: 'include',
			});
			const response: UpsertResponse = await update.json();
			if (response.status !== 200 && response.status !== 201) {
				throw response;
			}
			return response;
		},
		onMutate: async (wishGifted: Wish) => {
			const successToast = toast.success(
				t('wish.form.notification.success.gifted')
			);
			await utils.wish.get.cancel();

			const previousWishes = utils.wish.get.getData({
				id_list: parseInt(id_list as string),
			}) || {
				status: 200,
				wishes: [],
			};

			const optimisticWishes = previousWishes.wishes.filter(
				(wish) => wish.id !== wishGifted.id
			);

			utils.wish.get.setData(
				{ id_list: parseInt(id_list as string) },
				{ status: 200, wishes: optimisticWishes }
			);

			return { previousWishes, successToast };
		},
		onError(_, __, context) {
			if (!context?.previousWishes) return;
			utils.wish.get.setData(
				{ id_list: parseInt(id_list as string) },
				{ status: 200, wishes: context?.previousWishes.wishes }
			);
			toast.dismiss(context.successToast);
			toast.error(t('error.generic'));
		},
	});

	return {
		markAsGifted: mutate,
		markAsGiftedLoading: isPending,
	};
};

export default useMarkAsGifted;
